import { CheckCircle, Users, Star, CalendarClock, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn, getInitials } from '@/lib/utils';

interface AttendancePlayer {
  id: string;
  firstName: string;
  lastName: string;
  level: string | null;
  status: string;
  hasEvaluation: boolean;
  hasMakeupRequest: boolean;
}

interface GroupAttendanceListProps {
  players: AttendancePlayer[];
  onToggleAttendance: (playerId: string, currentStatus: string) => void;
  onOpenEvaluation: (player: AttendancePlayer, e: React.MouseEvent) => void;
  onRequestMakeup: (playerId: string, hasMakeupRequest: boolean, e: React.MouseEvent) => void;
  onAddPlayers: () => void;
}

export function GroupAttendanceList({
  players,
  onToggleAttendance,
  onOpenEvaluation,
  onRequestMakeup,
  onAddPlayers,
}: GroupAttendanceListProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Players
            </CardTitle>
            <CardDescription>Tap a player to cycle their status</CardDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onAddPlayers}
            className="gap-1.5"
          >
            <UserPlus className="w-4 h-4" />
            Add
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {players.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-3">
              No players added to this session yet
            </p>
            <Button
              size="sm"
              onClick={onAddPlayers}
              className="gap-1.5"
            >
              <UserPlus className="w-4 h-4" />
              Add Players
            </Button>
          </div>
        ) : (
          players.map(player => (
            <div
              key={player.id}
              onClick={() => onToggleAttendance(player.id, player.status)}
              className={cn(
                'flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition',
                player.status === 'present'
                  ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
                  : player.status === 'late'
                  ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/30'
                  : player.status === 'absent'
                  ? 'border-red-300 bg-red-50 dark:bg-red-950/30'
                  : 'border-border bg-card hover:bg-muted/40'
              )}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold text-sm">
                  {getInitials(player.firstName, player.lastName)}
                </div>
                <div>
                  <div className="font-medium text-foreground">
                    {player.firstName} {player.lastName}
                  </div>
                  <div className="text-sm text-muted-foreground capitalize">
                    {player.level || 'Unassigned'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(player.status === 'present' || player.status === 'late') && (
                  <Button
                    size="sm"
                    variant={player.hasEvaluation ? 'outline' : 'default'}
                    className={cn(
                      'h-8 px-2',
                      player.hasEvaluation
                        ? 'border-green-500 text-green-600 hover:bg-green-50'
                        : ''
                    )}
                    onClick={(e) => onOpenEvaluation(player, e)}
                  >
                    {player.hasEvaluation ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        <span className="text-xs">Done</span>
                      </>
                    ) : (
                      <>
                        <Star className="w-4 h-4 mr-1" />
                        <span className="text-xs">Eval</span>
                      </>
                    )}
                  </Button>
                )}
                {player.status === 'absent' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className={cn(
                      'h-8 px-2',
                      player.hasMakeupRequest
                        ? 'border-green-500 text-green-600 hover:bg-green-50'
                        : 'border-orange-400 text-orange-600 hover:bg-orange-50'
                    )}
                    onClick={(e) => onRequestMakeup(player.id, player.hasMakeupRequest, e)}
                  >
                    {player.hasMakeupRequest ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        <span className="text-xs">Makeup Requested</span>
                      </>
                    ) : (
                      <>
                        <CalendarClock className="w-4 h-4 mr-1" />
                        <span className="text-xs">Makeup</span>
                      </>
                    )}
                  </Button>
                )}
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center transition',
                    player.status === 'present'
                      ? 'bg-green-500'
                      : player.status === 'late'
                      ? 'bg-yellow-500'
                      : player.status === 'absent'
                      ? 'bg-red-400'
                      : 'bg-muted'
                  )}
                >
                  {player.status === 'present' && (
                    <CheckCircle className="w-5 h-5 text-white" />
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
